import { useEffect, useState, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Folder,
  File,
  Download,
  Trash2,
  Upload,
  RefreshCw,
  HardDrive,
  FileText,
  Image,
  Film,
  Music,
  Archive,
  AlertCircle,
  Eye,
  Construction,
  X,
  Plus,
  FolderOpen
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import { de } from 'date-fns/locale'

interface StorageFile {
  name: string
  id: string
  created_at: string
  updated_at: string
  last_accessed_at: string | null
  metadata: {
    size?: number
    mimetype?: string
    cacheControl?: string
    contentLength?: number
  }
}

interface BucketInfo {
  id: string
  name: string
  public: boolean
  created_at: string
  file_size_limit?: number
  allowed_mime_types?: string[]
}

export function Storage() {
  const [buckets, setBuckets] = useState<BucketInfo[]>([])
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null)
  const [files, setFiles] = useState<StorageFile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<StorageFile | null>(null)
  const [uploading, setUploading] = useState(false)
  const [currentPath, setCurrentPath] = useState('')
  const [previewFile, setPreviewFile] = useState<StorageFile | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetchBuckets()
  }, [])

  useEffect(() => {
    if (selectedBucket) {
      console.log('useEffect triggered - selectedBucket changed to:', selectedBucket)
      fetchFiles(selectedBucket, currentPath)
    }
  }, [selectedBucket])

  useEffect(() => {
    if (selectedBucket && currentPath !== undefined) {
      console.log('useEffect triggered - currentPath changed to:', currentPath)
      fetchFiles(selectedBucket, currentPath)
    }
  }, [currentPath])

  const fetchBuckets = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log('Fetching storage buckets...')

      // Try API first, then fallback to direct Supabase
      try {
        const isDev = import.meta.env.DEV
        const apiUrl = isDev ? 'https://ms-brain.vercel.app/api' : '/api'
        const endpoint = `${apiUrl}/storage/buckets`

        console.log('Trying API endpoint:', endpoint)

        const response = await fetch(endpoint)

        if (response.ok) {
          const result = await response.json()
          console.log('API success:', result)
          setBuckets(result.data || [])

          if (result.data && result.data.length > 0 && !selectedBucket) {
            setSelectedBucket(result.data[0].name)
          }
          return
        } else {
          console.log('API failed, status:', response.status)
          throw new Error('API endpoint failed')
        }
      } catch (apiError) {
        console.log('API fallback failed, trying direct Supabase:', apiError)

        // Fallback to direct Supabase call
        const { data, error } = await supabase.storage.listBuckets()

        console.log('Direct Supabase response:', { data, error })

        if (error) {
          console.error('Supabase storage error:', error)
          throw new Error(`Storage access denied: ${error.message}. Dies ist ein Admin-Feature das eine Konfiguration benötigt.`)
        }

        setBuckets(data || [])
        console.log('Set buckets from direct call:', data || [])

        // Auto-select first bucket if available
        if (data && data.length > 0 && !selectedBucket) {
          setSelectedBucket(data[0].name)
        }
      }
    } catch (error) {
      console.error('Error fetching buckets:', error)
      setError(`Fehler beim Laden der Storage-Buckets: ${error.message || error}`)
    } finally {
      setLoading(false)
    }
  }

  const fetchFiles = async (bucketName: string, path = '') => {
    try {
      setLoading(true)
      setError(null)

      console.log('Fetching files for bucket:', bucketName, 'path:', path)

      // Try API first, then fallback to direct Supabase
      try {
        const params = new URLSearchParams({ bucket: bucketName })
        if (path) params.set('path', path)

        const isDev = import.meta.env.DEV
        const apiUrl = isDev ? 'https://ms-brain.vercel.app/api' : '/api'
        const endpoint = `${apiUrl}/storage/files?${params}`

        console.log('Trying Files API endpoint:', endpoint)

        const response = await fetch(endpoint)

        if (response.ok) {
          const result = await response.json()
          console.log('Files API success:', result)
          setFiles(result.data || [])
          return
        } else {
          console.log('Files API failed, status:', response.status)
          throw new Error('Files API endpoint failed')
        }
      } catch (apiError) {
        console.log('Files API fallback failed, trying direct Supabase:', apiError)

        // Fallback to direct Supabase call
        console.log(`Calling supabase.storage.from("${bucketName}").list("${path}")`)

        const { data, error } = await supabase.storage
          .from(bucketName)
          .list(path, {
            limit: 100,
            offset: 0
          })

        console.log(`Direct files response for bucket "${bucketName}" path "${path}":`, { data, error })

        if (error) {
          console.error('Supabase files error:', error)
          // Don't throw error for empty buckets, just log it
          console.log(`Bucket ${bucketName} access issue: ${error.message}`)
          setFiles([])
          return
        }

        setFiles(data || [])
        console.log(`Set ${data?.length || 0} files from direct call for bucket ${bucketName}:`, data || [])
      }
    } catch (error) {
      console.error('Error fetching files:', error)
      setError(`Fehler beim Laden der Dateien: ${error.message || error}`)
    } finally {
      setLoading(false)
    }
  }

  const getFileIcon = (file: StorageFile) => {
    // Check if it's a folder (no mimetype and specific folder indicators)
    if (!file.metadata?.mimetype) {
      // Check if it's actually a folder by looking at the name and other properties
      if (file.name && !file.name.includes('.')) {
        return Folder
      }
      return File
    }

    const mimetype = file.metadata.mimetype
    const fileName = file.name.toLowerCase()

    // Check by file extension if MIME type is wrong
    if (fileName.endsWith('.png') || fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.gif') || fileName.endsWith('.webp') || fileName.endsWith('.svg')) {
      return Image
    }
    if (fileName.endsWith('.pdf')) {
      return FileText
    }
    if (fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.avi') || fileName.endsWith('.webm')) {
      return Film
    }
    if (fileName.endsWith('.mp3') || fileName.endsWith('.wav') || fileName.endsWith('.flac')) {
      return Music
    }

    // Fallback to MIME type
    if (mimetype.startsWith('image/')) return Image
    if (mimetype.startsWith('video/')) return Film
    if (mimetype.startsWith('audio/')) return Music
    if (mimetype.startsWith('text/')) return FileText
    if (mimetype === 'application/pdf') return FileText
    if (mimetype.includes('zip') || mimetype.includes('tar') || mimetype.includes('compressed')) return Archive
    if (mimetype.includes('json') || mimetype.includes('javascript') || mimetype.includes('typescript')) return FileText

    return File
  }

  const isFolder = (file: StorageFile) => {
    // A file is considered a folder if:
    // 1. No mimetype/size (typical folder indicators)
    // 2. Name doesn't contain file extension
    // 3. Metadata indicates it's a directory-like object
    return !file.metadata?.mimetype &&
           !file.metadata?.size &&
           file.name &&
           !file.name.includes('.')
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown'

    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  const downloadFile = async (bucketName: string, fileName: string) => {
    try {
      console.log('Downloading file:', fileName, 'from bucket:', bucketName)

      // Build the correct file path
      const filePath = currentPath ? `${currentPath}/${fileName}` : fileName
      console.log('Full file path:', filePath)

      const { data, error } = await supabase.storage
        .from(bucketName)
        .download(filePath)

      if (error) {
        console.error('Download error:', error)
        throw error
      }

      // Create download link
      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      console.log('Download successful for:', fileName)
    } catch (error) {
      console.error('Error downloading file:', error)
      setError(`Fehler beim Download der Datei "${fileName}": ${error.message || error}`)
    }
  }

  const uploadFiles = async (files: FileList) => {
    if (!selectedBucket || files.length === 0) return

    try {
      setUploading(true)
      setError(null)

      for (const file of Array.from(files)) {
        const filePath = currentPath ? `${currentPath}/${file.name}` : file.name

        const { error } = await supabase.storage
          .from(selectedBucket)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          })

        if (error) {
          if (error.message.includes('already exists')) {
            // Try to overwrite if file exists
            const { error: updateError } = await supabase.storage
              .from(selectedBucket)
              .update(filePath, file, {
                cacheControl: '3600',
                upsert: true
              })

            if (updateError) throw updateError
          } else {
            throw error
          }
        }
      }

      // Refresh file list
      await fetchFiles(selectedBucket, currentPath)
    } catch (error) {
      console.error('Error uploading files:', error)
      setError('Fehler beim Hochladen der Dateien')
    } finally {
      setUploading(false)
    }
  }

  const deleteFile = async (bucketName: string, fileName: string) => {
    try {
      const filePath = currentPath ? `${currentPath}/${fileName}` : fileName

      const { error } = await supabase.storage
        .from(bucketName)
        .remove([filePath])

      if (error) throw error

      // Refresh file list
      await fetchFiles(bucketName, currentPath)
    } catch (error) {
      console.error('Error deleting file:', error)
      setError('Fehler beim Löschen der Datei')
    }
  }

  const navigateToFolder = (folderName: string) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName
    console.log('=== FOLDER NAVIGATION ===')
    console.log('Folder name:', folderName)
    console.log('Current path before:', currentPath)
    console.log('New path:', newPath)
    console.log('Selected bucket:', selectedBucket)

    setCurrentPath(newPath)

    // Force immediate fetch without waiting for useEffect
    if (selectedBucket) {
      console.log('FORCE fetching files immediately for path:', newPath)
      fetchFiles(selectedBucket, newPath)
    } else {
      console.error('No selectedBucket available!')
    }
  }

  const navigateUp = () => {
    const pathParts = currentPath.split('/').filter(Boolean)
    pathParts.pop()
    const newPath = pathParts.join('/')
    setCurrentPath(newPath)
    if (selectedBucket) {
      fetchFiles(selectedBucket, newPath)
    }
  }

  const canPreview = (file: StorageFile) => {
    const mimetype = file.metadata?.mimetype
    const fileName = file.name.toLowerCase()

    // Check by file extension first (more reliable)
    if (fileName.endsWith('.png') || fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.gif') || fileName.endsWith('.webp')) {
      return true
    }
    if (fileName.endsWith('.pdf')) {
      return true
    }

    // Fallback to MIME type
    if (!mimetype) return false
    return mimetype.startsWith('image/') || mimetype === 'application/pdf'
  }

  const isImageFile = (file: StorageFile) => {
    const mimetype = file.metadata?.mimetype
    const fileName = file.name.toLowerCase()

    // Check by file extension first (more reliable than MIME type)
    return fileName.endsWith('.png') ||
           fileName.endsWith('.jpg') ||
           fileName.endsWith('.jpeg') ||
           fileName.endsWith('.gif') ||
           fileName.endsWith('.webp') ||
           fileName.endsWith('.svg') ||
           (mimetype && mimetype.startsWith('image/'))
  }

  const getPreviewUrl = async (bucketName: string, fileName: string) => {
    const filePath = currentPath ? `${currentPath}/${fileName}` : fileName

    const { data } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, 60 * 60) // 1 hour

    return data?.signedUrl
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-light tracking-tight flex items-center gap-3">
            <HardDrive className="h-8 w-8" />
            Storage Browser
            <Construction className="h-6 w-6 text-orange-500" />
          </h1>
          <p className="text-muted-foreground mt-1">
            Verwalten Sie Ihre Supabase Storage Buckets und Dateien
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => fetchBuckets()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Aktualisieren
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={!selectedBucket || uploading}
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                uploadFiles(e.target.files)
              }
            }}
          />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Buckets Sidebar */}
        <div className="col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Buckets</CardTitle>
              <CardDescription>
                {buckets.length} Storage Bucket{buckets.length !== 1 && 's'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <div className="p-4 space-y-2">
                  {loading && !buckets.length ? (
                    <>
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </>
                  ) : buckets.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Keine Buckets gefunden
                    </p>
                  ) : (
                    buckets.map(bucket => (
                      <Button
                        key={bucket.id}
                        variant={selectedBucket === bucket.name ? "default" : "ghost"}
                        className="w-full justify-start"
                        onClick={() => setSelectedBucket(bucket.name)}
                      >
                        <Folder className="mr-2 h-4 w-4" />
                        <span className="truncate">{bucket.name}</span>
                        {bucket.public && (
                          <Badge variant="secondary" className="ml-auto">
                            Public
                          </Badge>
                        )}
                      </Button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Files Grid */}
        <div className="col-span-9">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {selectedBucket ? `Dateien in "${selectedBucket}"` : 'Wählen Sie einen Bucket'}
                  </CardTitle>
                  <CardDescription>
                    {selectedBucket && `${files.length} Datei${files.length !== 1 ? 'en' : ''}`}
                  </CardDescription>
                </div>
                {currentPath && (
                  <Button onClick={navigateUp} variant="outline" size="sm">
                    <Folder className="mr-2 h-4 w-4" />
                    Zurück
                  </Button>
                )}
              </div>

              {/* Breadcrumb Navigation */}
              {currentPath && (
                <div className="flex items-center space-x-2 text-sm mt-2">
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-1 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setCurrentPath('')
                      if (selectedBucket) {
                        fetchFiles(selectedBucket, '')
                      }
                    }}
                  >
                    {selectedBucket}
                  </Button>
                  {currentPath.split('/').filter(Boolean).map((folder, index, array) => {
                    const isLast = index === array.length - 1
                    const pathToHere = array.slice(0, index + 1).join('/')

                    return (
                      <span key={index} className="flex items-center">
                        <span className="mx-2 text-muted-foreground">/</span>
                        {isLast ? (
                          <span className="text-foreground font-medium">
                            {folder}
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-1 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setCurrentPath(pathToHere)
                              if (selectedBucket) {
                                fetchFiles(selectedBucket, pathToHere)
                              }
                            }}
                          >
                            {folder}
                          </Button>
                        )}
                      </span>
                    )
                  })}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!selectedBucket ? (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <Folder className="h-16 w-16 mb-4 opacity-50" />
                  <p>Wählen Sie einen Bucket aus der Liste</p>
                </div>
              ) : loading ? (
                <div className="grid grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
              ) : files.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <File className="h-16 w-16 mb-4 opacity-50" />
                  <p>Keine Dateien in diesem Bucket</p>
                  <Button className="mt-4" variant="outline" disabled>
                    <Upload className="mr-2 h-4 w-4" />
                    Erste Datei hochladen
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {files.map(file => {
                    const FileIcon = getFileIcon(file)
                    const isFileFolder = isFolder(file)

                    return (
                      <Card
                        key={`${file.name}-${file.created_at}`}
                        className={cn(
                          "cursor-pointer transition-all hover:shadow-lg",
                          selectedFile?.id === file.id && "ring-2 ring-primary",
                          isFileFolder && "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800"
                        )}
                        onClick={() => {
                          if (isFileFolder) {
                            navigateToFolder(file.name)
                          } else {
                            setSelectedFile(file)
                          }
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex flex-col items-center space-y-3">
                            {!isFileFolder && isImageFile(file) ? (
                              <ImageThumbnail
                                bucketName={selectedBucket}
                                fileName={file.name}
                                currentPath={currentPath}
                                className="h-24 w-24 object-cover rounded-lg border border-muted"
                              />
                            ) : !isFileFolder && (file.metadata?.mimetype === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) ? (
                              <PDFThumbnail
                                bucketName={selectedBucket}
                                fileName={file.name}
                                currentPath={currentPath}
                                className="h-24 w-24 rounded-lg border border-muted bg-white"
                              />
                            ) : (
                              <FileIcon className={cn(
                                "h-12 w-12",
                                isFileFolder ? "text-blue-500" : "text-muted-foreground"
                              )} />
                            )}
                            <div className="w-full text-center">
                              <p className={cn(
                                "font-medium text-sm truncate",
                                isFileFolder && "text-blue-600 dark:text-blue-400"
                              )}>
                                {file.name}
                                {isFileFolder && " 📁"}
                              </p>
                              {!isFileFolder && (
                                <>
                                  <p className="text-xs text-muted-foreground">
                                    {formatFileSize(file.metadata?.size)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {file.metadata?.mimetype || 'Unknown type'}
                                  </p>
                                </>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {(() => {
                                  try {
                                    // Handle both ISO strings and Unix timestamps
                                    const date = typeof file.created_at === 'string' && file.created_at.includes('-')
                                      ? new Date(file.created_at)  // ISO string
                                      : new Date(Number(file.created_at) * 1000)  // Unix timestamp (seconds → ms)

                                    return formatDistanceToNow(date, {
                                      addSuffix: true,
                                      locale: de
                                    })
                                  } catch {
                                    return 'Zeitpunkt unbekannt'
                                  }
                                })()}
                              </p>
                            </div>
                            {!isFileFolder && (
                              <div className="flex gap-2 w-full">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    downloadFile(selectedBucket, file.name)
                                  }}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={async (e) => {
                                    e.stopPropagation()
                                    if (canPreview(file)) {
                                      setPreviewFile(file)
                                    }
                                  }}
                                  disabled={!canPreview(file)}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (confirm(`Möchten Sie "${file.name}" wirklich löschen?`)) {
                                      deleteFile(selectedBucket, file.name)
                                    }
                                  }}
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              </div>
                            )}
                            {isFileFolder && (
                              <div className="text-xs text-blue-500 font-medium">
                                Ordner - Klicken zum Öffnen
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {previewFile?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewFile && selectedBucket && (
              <PreviewComponent
                bucketName={selectedBucket}
                file={previewFile}
                currentPath={currentPath}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ImageThumbnail({ bucketName, fileName, currentPath, className }: {
  bucketName: string
  fileName: string
  currentPath: string
  className?: string
}) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        setLoading(true)
        setError(false)

        const filePath = currentPath ? `${currentPath}/${fileName}` : fileName

        const { data } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(filePath, 60 * 60) // 1 hour

        if (data?.signedUrl) {
          setThumbnailUrl(data.signedUrl)
        } else {
          setError(true)
        }
      } catch (error) {
        console.error('Error loading thumbnail:', error)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    loadThumbnail()
  }, [bucketName, fileName, currentPath])

  if (loading) {
    return (
      <div className={cn("bg-muted animate-pulse rounded-lg", className)} />
    )
  }

  if (error || !thumbnailUrl) {
    return (
      <div className={cn("bg-muted rounded-lg flex items-center justify-center", className)}>
        <Image className="h-6 w-6 text-muted-foreground" />
      </div>
    )
  }

  return (
    <img
      src={thumbnailUrl}
      alt={fileName}
      className={className}
      onError={() => setError(true)}
      loading="lazy"
    />
  )
}

function PDFThumbnail({ bucketName, fileName, currentPath, className }: {
  bucketName: string
  fileName: string
  currentPath: string
  className?: string
}) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const loadPdfThumbnail = async () => {
      try {
        setLoading(true)
        setError(false)

        const filePath = currentPath ? `${currentPath}/${fileName}` : fileName

        const { data } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(filePath, 60 * 60) // 1 hour

        if (data?.signedUrl) {
          setPdfUrl(data.signedUrl)
        } else {
          setError(true)
        }
      } catch (error) {
        console.error('Error loading PDF thumbnail:', error)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    loadPdfThumbnail()
  }, [bucketName, fileName, currentPath])

  if (loading) {
    return (
      <div className={cn("bg-muted animate-pulse rounded-lg", className)} />
    )
  }

  if (error || !pdfUrl) {
    return (
      <div className={cn("bg-muted rounded-lg flex items-center justify-center", className)}>
        <FileText className="h-6 w-6 text-red-500" />
      </div>
    )
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <iframe
        src={`${pdfUrl}#page=1&zoom=50`}
        className="w-full h-full border-0 pointer-events-none scale-110 origin-top-left"
        title={`${fileName} thumbnail`}
        onError={() => setError(true)}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none" />
      <div className="absolute bottom-1 right-1">
        <FileText className="h-3 w-3 text-white drop-shadow-sm" />
      </div>
    </div>
  )
}

function PreviewComponent({ bucketName, file, currentPath }: {
  bucketName: string
  file: StorageFile
  currentPath: string
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadPreview = async () => {
      try {
        setLoading(true)
        setError(null)

        const filePath = currentPath ? `${currentPath}/${file.name}` : file.name

        const { data } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(filePath, 60 * 60) // 1 hour

        if (data?.signedUrl) {
          setPreviewUrl(data.signedUrl)
        } else {
          setError('Vorschau konnte nicht geladen werden')
        }
      } catch (error) {
        console.error('Error creating preview URL:', error)
        setError('Fehler beim Laden der Vorschau')
      } finally {
        setLoading(false)
      }
    }

    loadPreview()
  }, [bucketName, file.name, currentPath])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Lade Vorschau...</p>
        </div>
      </div>
    )
  }

  if (error || !previewUrl) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-muted-foreground">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{error || 'Vorschau nicht verfügbar'}</p>
        </div>
      </div>
    )
  }

  if (file.metadata?.mimetype?.startsWith('image/')) {
    return (
      <div className="flex justify-center">
        <img
          src={previewUrl}
          alt={file.name}
          className="max-w-full max-h-[70vh] object-contain rounded-lg"
          onError={() => setError('Bild konnte nicht geladen werden')}
        />
      </div>
    )
  }

  if (file.metadata?.mimetype === 'application/pdf') {
    return (
      <div className="w-full h-[70vh]">
        <iframe
          src={previewUrl}
          className="w-full h-full rounded-lg border"
          title={file.name}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center h-96">
      <div className="text-center text-muted-foreground">
        <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Vorschau für diesen Dateityp nicht unterstützt</p>
        <Button
          className="mt-4"
          onClick={() => window.open(previewUrl, '_blank')}
        >
          In neuem Tab öffnen
        </Button>
      </div>
    </div>
  )
}